import { Button, Group, Text } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InitialBalancesAuditList } from 'src/Components/Expenses/InitialBalancesAuditList';
import { CreateReminderModal } from 'src/Components/Modals/CreateReminderModal';
import { EditInitialBalanceModal } from 'src/Components/Modals/EditInitialBalanceModal';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchAssetMemberBalances from 'src/Hooks/Queries/useFetchAssetMemberBalances';
import useFetchInitialBalances from 'src/Hooks/Queries/useFetchInitialBalances';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { AssetMemberBalanceDto, InitialBalanceDto } from 'src/Services/API/generated';
import { AssetGovernanceFields, OperationalRole } from 'src/types/governance.types';
import { formatClosingDate } from 'src/Utils/formatClosingDate';

import styles from './PropertyBalanceTable.module.scss';

const formatAmount = (amount: number): string =>
  amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const amountClass = (amount: number): string =>
  amount < 0 ? styles.amountNegative : styles.amountPositive;

const PropertyBalanceTable = () => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data, isLoading } = useFetchAssetMemberBalances();
  const { data: initialBalances } = useFetchInitialBalances();
  const { data: asset } = useFetchAssetDetails();
  const [editingBalance, setEditingBalance] = useState<InitialBalanceDto | null>(null);
  const [reminderTarget, setReminderTarget] = useState<AssetMemberBalanceDto | null>(null);

  // Tous les soldes initiaux d'un bien partagent la même date d'arrêté.
  const pivotDate = initialBalances?.[0]?.asOfDate;
  const closingDateLabel = pivotDate ? formatClosingDate(pivotDate, i18n.language) : undefined;

  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const isGestionnaire = governance?.currentUserOperationalRole === OperationalRole.GESTIONNAIRE;

  // Sans date de clôture, il n'y a ni position d'ouverture ni « depuis » à afficher : le bien
  // n'a qu'un seul chiffre par membre, son solde du jour.
  const closingColumns = closingDateLabel
    ? [
        {
          accessor: 'initialBalance',
          title: t('expenses.propertyBalanceTable.balanceAt', 'Solde au {{date}}', {
            date: closingDateLabel,
          }),
          render: (record: AssetMemberBalanceDto) => (
            <span className={amountClass(record.initialBalance)}>
              {formatAmount(record.initialBalance)}
            </span>
          ),
        },
        {
          accessor: 'activity',
          title: t('expenses.propertyBalanceTable.since', 'Depuis le {{date}}', {
            date: closingDateLabel,
          }),
          render: (record: AssetMemberBalanceDto) => (
            <span className={amountClass(record.activity)}>{formatAmount(record.activity)}</span>
          ),
        },
      ]
    : [];

  return (
    <div className={styles.container}>
      <Text fw={600} mb="xs">
        {t('expenses.propertyBalanceTable.title', 'Soldes de tous les membres')}
      </Text>
      <DataTable
        withRowBorders={false}
        records={data ?? []}
        idAccessor="assetMemberId"
        fetching={isLoading}
        noRecordsText={t('expenses.propertyBalanceTable.empty', 'Aucun membre')}
        columns={[
          {
            accessor: 'name',
            title: t('expenses.propertyBalanceTable.member', 'Membre'),
            render: record => record.displayName,
          },
          ...closingColumns,
          {
            accessor: 'netBalance',
            title: t('expenses.propertyBalanceTable.today', "Solde aujourd'hui"),
            render: record => (
              <span className={amountClass(record.netBalance)}>
                <b>{formatAmount(record.netBalance)}</b>
              </span>
            ),
          },
          {
            accessor: 'actions',
            title: '',
            hidden: !isGestionnaire,
            render: record => {
              const balance = record.userId
                ? initialBalances?.find(b => b.userId === record.userId)
                : undefined;
              return (
                <Group gap="xs" wrap="nowrap">
                  {balance && (
                    <Button variant="outline" size="xs" onClick={() => setEditingBalance(balance)}>
                      {t('expenses.propertyBalanceTable.edit', 'Modifier')}
                    </Button>
                  )}
                  {record.netBalance < 0 && (
                    <Button
                      variant="light"
                      color="orange"
                      size="xs"
                      onClick={() => setReminderTarget(record)}
                    >
                      {t('expenses.propertyBalanceTable.remind', 'Relancer')}
                    </Button>
                  )}
                </Group>
              );
            },
          },
        ]}
      />
      <InitialBalancesAuditList />
      {assetId && (
        <EditInitialBalanceModal
          open={!!editingBalance}
          onClose={() => setEditingBalance(null)}
          assetId={assetId}
          initialBalance={editingBalance}
        />
      )}
      <CreateReminderModal
        open={!!reminderTarget}
        onClose={() => setReminderTarget(null)}
        assetMemberId={reminderTarget?.assetMemberId ?? ''}
        memberName={reminderTarget?.displayName ?? ''}
        netBalance={reminderTarget?.netBalance ?? 0}
      />
    </div>
  );
};

export default PropertyBalanceTable;
